import type { ReactNode } from 'react'

import type { ItemSalesReport, ItemSalesRow } from '../types'
import { itemGroupName, ItemSalesReading } from './item-sales-reading'
import { ReportTable } from './report-table'
import type { ReportColumn, ReportRow } from './report-table'

const ITEM: ReportColumn<ItemSalesRow> = {
  id: 'item',
  header: 'Item',
  value: (row) => row.name,
  render: (row) => (
    <span className="flex flex-col">
      <span>
        {row.name}
        {/* The box or size it went out as. The legacy pack split BABY FRIES
            into two unrelated rows by box; here it is one item, labelled. */}
        {row.variantName && <span className="text-muted"> · {row.variantName}</span>}
      </span>
      {row.sku && <span className="text-muted text-xs">{row.sku}</span>}
    </span>
  ),
}

const SOLD: ReportColumn<ItemSalesRow> = {
  id: 'quantity',
  header: 'Sold',
  format: 'quantity',
  value: (row) => row.quantity,
}

const COMPED: ReportColumn<ItemSalesRow> = {
  id: 'compQuantity',
  header: 'Comped',
  format: 'quantity',
  value: (row) => row.compQuantity,
  hideWhenEmpty: true,
}

const NET: ReportColumn<ItemSalesRow> = {
  id: 'net',
  header: 'Net',
  format: 'usd',
  value: (row) => row.net,
}

const COLUMNS: Record<ItemSalesReading, ReportColumn<ItemSalesRow>[]> = {
  [ItemSalesReading.Sales]: [
    ITEM,
    { id: 'unit', header: 'Unit', value: (row) => row.unit, hideWhenEmpty: true },
    SOLD,
    COMPED,
    {
      id: 'compValue',
      header: 'Comp value',
      format: 'usd',
      value: (row) => row.compValue,
      hideWhenEmpty: true,
    },
    { id: 'total', header: 'Total', format: 'usd', value: (row) => row.totalAmount },
    {
      id: 'discount',
      header: 'Discount',
      format: 'usd',
      value: (row) => row.discount,
      hideWhenEmpty: true,
    },
    NET,
    { id: 'share', header: '% of sales', format: 'share', value: (row) => row.shareOfSales },
  ],

  /* A blank cost prints a dash, never $0.00 and a 100% margin — the legacy
     Gross Profit report's one figure, and never true. */
  [ItemSalesReading.Profit]: [
    ITEM,
    SOLD,
    COMPED,
    NET,
    { id: 'cost', header: 'Cost', format: 'usd', value: (row) => row.cost ?? null },
    { id: 'margin', header: 'Margin', format: 'usd', value: (row) => row.margin ?? null },
    {
      id: 'marginPercent',
      header: 'Margin %',
      format: 'share',
      value: (row) => row.marginPercent ?? null,
    },
    {
      id: 'costCoverage',
      header: 'Costed',
      format: 'share',
      value: (row) => row.costCoverage ?? 0,
      // Only worth a column when some sales went without a cost. When every
      // line had one it would be a column of 100%.
      visibleWhen: (rows) => rows.some((row) => (row.costCoverage ?? 0) < 1),
    },
  ],

  [ItemSalesReading.Incentive]: [
    ITEM,
    SOLD,
    NET,
    { id: 'incentive', header: 'Incentive', format: 'usd', value: (row) => row.incentive ?? 0 },
  ],
}

/**
 * The item report: what sold, in blocks — by category, cashier or customer —
 * biggest first.
 *
 * Comps are counted AND valued. The legacy pack showed a free item as a $0
 * line with FOC = 0, so a kitchen giving away forty portions a week looked
 * exactly like one giving away none.
 *
 * Cost and incentive are read off each line as it was SOLD, never the product
 * today, so a cost changed this week does not rewrite last month's margin.
 */
export function ItemSalesTable({
  report,
  reading = ItemSalesReading.Sales,
  showTotal = true,
  totalLabel = 'All items',
  footer,
}: {
  report: ItemSalesReport
  reading?: ItemSalesReading
  /**
   * Off when the groups are a filter's selection: "All items" over one
   * category would label that category's figures as everything.
   */
  showTotal?: boolean
  totalLabel?: string
  footer?: ReactNode
}) {
  const rows: ReportRow<ItemSalesRow>[] = report.groups.flatMap((group) => {
    const name = itemGroupName(group)

    return [
      { id: `${name}:head`, data: null, label: name, tone: 'group' as const },
      ...group.items.map((item, index) => ({
        id: `${name}:${item.sku ?? item.name}:${item.variantName ?? ''}:${index}`,
        data: item,
      })),
      {
        id: `${name}:subtotal`,
        data: group.subtotal,
        label: `${name} total`,
        tone: 'subtotal' as const,
      },
    ]
  })

  if (showTotal) rows.push({ id: 'total', data: report.total, label: totalLabel, tone: 'total' })

  return (
    <ReportTable
      ariaLabel={report.meta.title}
      columns={COLUMNS[reading]}
      rows={rows}
      footer={footer}
    />
  )
}
